import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import useAuth from "../../../../hooks/useAuth";

/** Password rules */
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

const PASSWORD_ERROR_MESSAGE =
  "Password must be at least 8 characters and contain at least one uppercase, one lowercase, one number, and one special character (@$!%*?&#)";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [open, setOpen] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const { getResetPasswordConfirmation, postResetPassword } = useAuth({
    email: searchParams.get("email") ?? "",
    token: searchParams.get("token") ?? "",
  });

  /** Validate token on mount */
  useEffect(() => {
    getResetPasswordConfirmation.refetch();
  }, []);

  /** Close dialog if token invalid */
  useEffect(() => {
    if (getResetPasswordConfirmation.isError) {
      handleClose();
    }
  }, [getResetPasswordConfirmation.isError]);

  const handleClose = () => {
    setOpen(false);
    navigate("/home");
  };

  /** Password validation */
  const validatePassword = (value: string) => {
    if (!value) {
      setPasswordError(null);
      return false;
    }

    if (!PASSWORD_REGEX.test(value)) {
      setPasswordError(PASSWORD_ERROR_MESSAGE);
      return false;
    }

    setPasswordError(null);
    return true;
  };

  const handleSubmit = () => {
    if (!validatePassword(password)) return;

    postResetPassword.mutate(
      {
        newPassword: password,
        email: searchParams.get("email") ?? "",
        token: searchParams.get("token") ?? "",
      },
      {
        onSuccess: () => handleClose(),
      }
    );
  };

  /** Debounced validation (optional) */
  const debouncedValidatePassword = useMemo(
    () =>
      debounce((value: string) => {
        validatePassword(value);
      }, 300),
    []
  );

  return (
    <Stack>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Set your new password for <b>{searchParams.get("email") ?? ""}</b>
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="New password"
            placeholder="Enter new password"
            margin="dense"
            value={password}
            error={Boolean(passwordError)}
            helperText={passwordError}
            onChange={(e) => {
              setPassword(e.target.value);
              debouncedValidatePassword(e.target.value);
            }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !password || Boolean(passwordError) || postResetPassword.isLoading
            }
          >
            {postResetPassword.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Set new password"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ResetPassword;
